import { useCallback, useEffect, useSyncExternalStore } from 'react'

/**
 * Theme preference — three states, not two.
 *
 * `system` is the default and follows the OS, which is exactly what the app did
 * before this existed: anyone who never touches the control sees no change.
 * `light` and `dark` are explicit overrides, and the tool needs them because the
 * same person reads it on a bright desk in the office and on a dark-themed
 * phone in an empty flat on the same afternoon — one OS setting cannot be right
 * for both.
 *
 * Only the RESOLVED theme ever reaches the DOM, as `<html data-theme>`. That is
 * what lets index.css keep a single dark block instead of one copy per
 * selector, and it is why "system" is resolved here rather than in CSS.
 *
 * THE KEY AND THE RESOLUTION RULE ARE DUPLICATED in index.html's pre-paint
 * script, which has to run before the first frame — long before this module has
 * even been downloaded. Change one, change the other.
 */
export const THEME_STORAGE_KEY = 'acasa.theme.v1'

/** Order is the order of the segmented control. German, like all UI copy. */
export const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Hell' },
  { value: 'dark', label: 'Dunkel' },
] as const

export type ThemePreference = (typeof THEME_OPTIONS)[number]['value']
export type ResolvedTheme = 'light' | 'dark'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isPreference(stored) ? stored : 'system'
  } catch {
    // Private browsing or blocked storage — following the OS is a fine default,
    // and a colour scheme is never worth breaking the page over.
    return 'system'
  }
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

/*
 * One module-level store rather than state per hook call.
 *
 * The rail and the Einstellungen page both render a control, and on a desktop
 * they are on screen at the same time — two useState copies would leave one of
 * them highlighting a segment the user had just moved away from. An external
 * store fixes that without wrapping the tree in another provider.
 */
type ThemeState = { preference: ThemePreference; resolved: ResolvedTheme }

const listeners = new Set<() => void>()
let state: ThemeState = snapshotFor(readPreference())

function snapshotFor(preference: ThemePreference): ThemeState {
  return { preference, resolved: resolve(preference) }
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Identity is stable between changes, which is what useSyncExternalStore wants. */
function getSnapshot(): ThemeState {
  return state
}

function commit(preference: ThemePreference) {
  const next = snapshotFor(preference)
  if (next.preference === state.preference && next.resolved === state.resolved) return

  state = next
  applyTheme(next.resolved)
  for (const listener of listeners) listener()
}

/**
 * Repaints the mobile browser's own chrome to match the canvas.
 *
 * `<meta name="theme-color">` cannot reference a custom property, so the token
 * is read live — but the palette is authored in oklch and the meta tag predates
 * it, so the value is RASTERISED through a 1x1 canvas and read back as bytes
 * rather than trusting a serialisation. Chrome hands `fillStyle` back as the
 * same `oklch(...)` string it was given, which is exactly the value the meta
 * tag cannot use.
 *
 * Reading the token instead of hard-coding two hexes keeps this from becoming a
 * second copy of the palette — the drift index.css is organised to prevent.
 */
function syncBrowserChrome() {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) return

  const token = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim()
  if (!token) return

  const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  if (!context) return

  // A colour the parser rejects leaves fillStyle at the sentinel, which is the
  // failure check — the canvas token is never pure black in either theme.
  context.fillStyle = '#000000'
  context.fillStyle = token
  if (context.fillStyle === '#000000') return

  context.fillRect(0, 0, 1, 1)
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data
  if (r === undefined || g === undefined || b === undefined) return

  const hex = [r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')
  meta.setAttribute('content', `#${hex}`)
}

/**
 * Mirrors the resolved theme onto `<html>`.
 *
 * `data-theme-switching` suppresses transitions for the two frames the swap
 * takes — see the rule in index.css. On first mount the attribute already holds
 * the right value (the pre-paint script put it there), so nothing flickers and
 * only the browser chrome is left to sync.
 */
function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement

  if (root.dataset.theme !== resolved) {
    root.setAttribute('data-theme-switching', '')
    root.dataset.theme = resolved
    requestAnimationFrame(() => {
      requestAnimationFrame(() => root.removeAttribute('data-theme-switching'))
    })
  }

  syncBrowserChrome()
}

// Registered once for the life of the tab: `system` has to keep tracking the
// OS, and an explicit choice has to keep ignoring it.
window.matchMedia(DARK_QUERY).addEventListener('change', () => {
  if (state.preference === 'system') commit('system')
})

// The matrix in one tab and a Protokoll in another is the normal way this tool
// is used; a preference that only took effect in the tab it was set in would
// look broken.
window.addEventListener('storage', (event) => {
  if (event.key === THEME_STORAGE_KEY) commit(readPreference())
})

export function useTheme() {
  const { preference, resolved } = useSyncExternalStore(subscribe, getSnapshot)

  // `data-theme` is already correct here — the pre-paint script set it. This is
  // for the browser chrome, which that script cannot tint: the tokens it reads
  // only exist once index.css has been applied, which is after this module was
  // evaluated.
  useEffect(() => {
    syncBrowserChrome()
  }, [])

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Unavailable storage costs the choice on the next reload, nothing more.
    }
    commit(next)
  }, [])

  return { preference, resolved, setPreference }
}
